import React, { useEffect, useState } from "react";
import { useAtom, useAtomValue } from "../app-jotai";
import {
  activeCanvasAtom,
  authSessionAtom,
  saveCanvasDialogStateAtom,
  cloudSaveStatusAtom,
} from "./auth-state";
import "./ActiveCanvasBadge.scss";

export const ActiveCanvasBadge: React.FC<{
  onSave?: () => Promise<void> | void;
  onNewCanvas?: () => void;
  isSaving?: boolean;
}> = ({ onSave, onNewCanvas, isSaving = false }) => {
  const [session] = useAtom(authSessionAtom);
  const [activeCanvas, setActiveCanvas] = useAtom(activeCanvasAtom);
  const [, setSaveCanvasDialog] = useAtom(saveCanvasDialogStateAtom);
  const cloudSaveStatus = useAtomValue(cloudSaveStatusAtom);

  const [recentlySaved, setRecentlySaved] = useState(false);

  useEffect(() => {
    if (cloudSaveStatus.status === "saved") {
      setRecentlySaved(true);
      const timer = window.setTimeout(() => {
        setRecentlySaved(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [cloudSaveStatus]);

  if (!session.user) {
    return null;
  }

  const isCurrentlySaving = isSaving || cloudSaveStatus.status === "saving";

  let statusText = "Ctrl+S";
  let statusClass = "";

  if (isCurrentlySaving) {
    statusText = "Saving...";
    statusClass = "is-status-saving";
  } else if (recentlySaved) {
    statusText = "Saved to Cloud";
    statusClass = "is-status-saved";
  } else if (cloudSaveStatus.status === "error") {
    statusText = "Error saving";
    statusClass = "is-status-error";
  }

  if (activeCanvas) {
    return (
      <div className="ActiveCanvasBadge-wrapper">
        <button
          type="button"
          className={`ActiveCanvasBadge is-active ${isCurrentlySaving ? "is-saving" : ""} ${recentlySaved ? "has-saved" : ""}`}
          onClick={onSave}
          title={
            recentlySaved
              ? "Successfully saved to Cloud!"
              : `Saved: "${activeCanvas.name}". Click or press Ctrl+S to save changes.`
          }
        >
          <span className="ActiveCanvasBadge__name">{activeCanvas.name}</span>
          <span className={`ActiveCanvasBadge__status ${statusClass}`}>
            {statusText}
          </span>
        </button>
        <button
          type="button"
          className="ActiveCanvasBadge__close-btn"
          onClick={(e) => {
            e.stopPropagation();
            setActiveCanvas(null);
            onNewCanvas?.();
          }}
          title="Close canvas and start new blank whiteboard"
          aria-label="Close canvas"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`ActiveCanvasBadge is-unsaved ${recentlySaved ? "has-saved" : ""}`}
      onClick={() => setSaveCanvasDialog({ isOpen: true })}
      title="Save this drawing to your Cloud Canvases (Ctrl+S)"
    >
      <span className="ActiveCanvasBadge__name">Save to Cloud</span>
      <span className={`ActiveCanvasBadge__status ${statusClass}`}>
        {statusText}
      </span>
    </button>
  );
};
